import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
    Lock, Eye, EyeOff, AlertCircle, CheckCircle,
    ShieldAlert, Check, X, Loader2
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { apiMethods } from '@/services/api';

const ChangePassword = () => {
    const navigate = useNavigate();
    const { user, updateUser } = useAuthStore(); // Added updateUser
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({});

    // Check if this is a forced password change
    const isForced = user?.mustChangePassword || false;

    // Password requirements - based on backend validation
    const passwordRequirements = [
        { regex: /.{6,}/, text: 'At least 6 characters', required: true }
    ];

    // Additional recommendations (not enforced by backend)
    const passwordRecommendations = [
        { regex: /[A-Z]/, text: 'One uppercase letter (recommended)' },
        { regex: /[a-z]/, text: 'One lowercase letter (recommended)' },
        { regex: /[0-9]/, text: 'One number (recommended)' },
        { regex: /[!@#$%^&*]/, text: 'One special character (recommended)' }
    ];

    const checkPasswordStrength = (password) => {
        const required = passwordRequirements.map(req => ({
            ...req,
            met: req.regex.test(password),
            isRequired: true
        }));

        const recommended = passwordRecommendations.map(req => ({
            ...req,
            met: req.regex.test(password),
            isRequired: false
        }));

        return [...required, ...recommended];
    };

    const changePasswordMutation = useMutation({
        mutationFn: async (data) => {
            const response = await apiMethods.auth.changePassword(data);
            return response.data;
        },
        onSuccess: () => {
            // If forced password change, update user state
            // Clear both copies of the flag; PrivateRoute reads the top-level one.
            updateUser({ mustChangePassword: false });
            useAuthStore.setState({ mustChangePassword: false });

            // Navigate to dashboard after short delay to show success
            setTimeout(() => {
                navigate('/user/dashboard');
            }, 2000);
        },
        onError: (error) => {
            const message = error.response?.data?.message || 'Failed to change password';
            if (message.toLowerCase().includes('incorrect') || message.toLowerCase().includes('invalid')) {
                setErrors({ currentPassword: 'Current password is incorrect' });
            } else {
                setErrors({ general: message });
            }
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Clear errors when user types
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.currentPassword) {
            newErrors.currentPassword = 'Current password is required';
        }

        if (!formData.newPassword) {
            newErrors.newPassword = 'New password is required';
        } else if (formData.newPassword.length < 6) {
            newErrors.newPassword = 'New password must be at least 6 characters long';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        if (formData.currentPassword === formData.newPassword) {
            newErrors.newPassword = 'New password must be different from current password';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            changePasswordMutation.mutate({
                currentPassword: formData.currentPassword,
                newPassword: formData.newPassword
            });
        }
    };

    const passwordStrength = checkPasswordStrength(formData.newPassword);

    if (changePasswordMutation.isSuccess) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
                <div className="max-w-md w-full">
                    <div className="text-center">
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                            <CheckCircle className="h-6 w-6 text-green-600" />
                        </div>
                        <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                            Password Changed Successfully
                        </h2>
                        <p className="mt-2 text-sm text-gray-600">
                            Redirecting to dashboard...
                        </p>
                        <div className="mt-4">
                            <Loader2 className="animate-spin h-6 w-6 text-primary-600 mx-auto" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {isForced && (
                    <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex">
                            <ShieldAlert className="h-5 w-5 text-yellow-600 mt-0.5" />
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-yellow-800">
                                    Password Change Required
                                </h3>
                                <p className="mt-1 text-sm text-yellow-700">
                                    You must change your password before continuing to use your account.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-white shadow rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h1 className="text-2xl font-bold text-gray-900">Change Password</h1>
                        <p className="mt-1 text-sm text-gray-600">
                            Update your password to keep your account secure
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 space-y-6">
                        {errors.general && (
                            <div className="rounded-md bg-red-50 p-4">
                                <div className="flex">
                                    <AlertCircle className="h-5 w-5 text-red-400" />
                                    <p className="ml-3 text-sm text-red-800">{errors.general}</p>
                                </div>
                            </div>
                        )}

                        {/* Current Password */}
                        <div>
                            <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                                Current Password
                            </label>
                            <div className="mt-1 relative">
                                <input
                                    id="currentPassword"
                                    name="currentPassword"
                                    type={showCurrentPassword ? 'text' : 'password'}
                                    value={formData.currentPassword}
                                    onChange={handleChange}
                                    className={`appearance-none block w-full px-3 py-2 pr-10 border ${
                                        errors.currentPassword ? 'border-red-300' : 'border-gray-300'
                                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                                    placeholder="Enter current password"
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                >
                                    {showCurrentPassword ? (
                                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                                    ) : (
                                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                                    )}
                                </button>
                            </div>
                            {errors.currentPassword && (
                                <p className="mt-1 text-sm text-red-600">{errors.currentPassword}</p>
                            )}
                        </div>

                        {/* New Password */}
                        <div>
                            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                                New Password
                            </label>
                            <div className="mt-1 relative">
                                <input
                                    id="newPassword"
                                    name="newPassword"
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={formData.newPassword}
                                    onChange={handleChange}
                                    className={`appearance-none block w-full px-3 py-2 pr-10 border ${
                                        errors.newPassword ? 'border-red-300' : 'border-gray-300'
                                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                                    placeholder="Enter new password"
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                >
                                    {showNewPassword ? (
                                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                                    ) : (
                                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                                    )}
                                </button>
                            </div>
                            {errors.newPassword && (
                                <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
                            )}

                            {/* Password Requirements */}
                            {formData.newPassword && (
                                <div className="mt-3 p-3 bg-gray-50 rounded-md">
                                    <p className="text-xs font-medium text-gray-700 mb-2">Password requirements:</p>
                                    <ul className="space-y-1">
                                        {passwordStrength.map((req, index) => (
                                            <li key={index} className="flex items-center text-xs">
                                                {req.met ? (
                                                    <Check className="h-3 w-3 text-green-500 mr-2 flex-shrink-0" />
                                                ) : (
                                                    <X className={`h-3 w-3 ${req.isRequired ? 'text-red-400' : 'text-gray-400'} mr-2 flex-shrink-0`} />
                                                )}
                                                <span className={
                                                    req.met
                                                        ? 'text-green-700'
                                                        : req.isRequired
                                                            ? 'text-red-600 font-medium'
                                                            : 'text-gray-600'
                                                }>
                                                    {req.text}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* Confirm Password */}
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                Confirm New Password
                            </label>
                            <div className="mt-1 relative">
                                <input
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    className={`appearance-none block w-full px-3 py-2 pr-10 border ${
                                        errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                                    placeholder="Confirm new password"
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? (
                                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                                    ) : (
                                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                                    )}
                                </button>
                            </div>
                            {errors.confirmPassword && (
                                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                            )}
                        </div>

                        {/* Form Actions */}
                        <div className="flex items-center justify-end space-x-3 pt-4">
                            {!isForced && (
                                <button
                                    type="button"
                                    onClick={() => navigate('/user/dashboard')}
                                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                >
                                    Cancel
                                </button>
                            )}
                            <button
                                type="submit"
                                disabled={changePasswordMutation.isPending}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {changePasswordMutation.isPending ? (
                                    <>
                                        <Loader2 className="animate-spin h-4 w-4 mr-2" />
                                        Changing...
                                    </>
                                ) : (
                                    <>
                                        <Lock className="h-4 w-4 mr-2" />
                                        Change Password
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Security Tips */}
                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-blue-900 mb-2">
                        Security Tips
                    </h3>
                    <ul className="text-sm text-blue-700 space-y-1">
                        <li>• Use a unique password that you don't use for other accounts</li>
                        <li>• Consider using a password manager to generate and store secure passwords</li>
                        <li>• Enable two-factor authentication for additional security</li>
                        <li>• Never share your password with anyone</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;